import { Tooltip, TooltipContent, TooltipOrigin } from '@silo/ui';
import { Legend, LegendItem } from './LegendItems';
import { formatAsPreciseDecimalsWithDecimals, pointsToPrecisePctWithDecimals } from '@silo/utils';

type Trend =
  | {
      direction: 'positive' | 'negative';
      value: number;
      average: number;
    }
  | { direction: 'none' };

type Props = {
  depositAPY?: string;
  borrowAPY?: string;
  averageAPY?: string;
  trend?: {
    deposit?: Trend;
    borrow?: Trend;
  };
  hourly?: boolean;
};

const trendDirectionToVerb = (trend: Trend['direction']) =>
  trend === 'positive' ? 'rising' : trend === 'negative' ? 'falling' : 'flat';

export const ApyChartLegend = (props: Props) => {
  const { trend, hourly } = props;

  const deposit = props.depositAPY ? (
    <LegendItem value={props.depositAPY} label="Deposit APR" align="left" color="bg-violet-400" />
  ) : null;

  const borrow = props.borrowAPY ? (
    <LegendItem value={props.borrowAPY} label="Borrow APR" align="left" color="bg-violet-400" />
  ) : null;

  return (
    <Legend>
      {deposit ? (
        trend?.deposit && trend.deposit.direction !== 'none' ? (
          <Tooltip placement="bottom">
            <TooltipOrigin className="even:pl-6">{deposit}</TooltipOrigin>
            <TooltipContent>
              <Label
                direction={trend.deposit.direction}
                value={trend.deposit.value}
                hourly={hourly}
                apy="deposit"
                average={trend.deposit.average}
              />
            </TooltipContent>
          </Tooltip>
        ) : (
          deposit
        )
      ) : null}
      {borrow ? (
        trend?.borrow && trend.borrow.direction !== 'none' ? (
          <Tooltip placement="bottom">
            <TooltipOrigin className="even:pl-6">{borrow}</TooltipOrigin>
            <TooltipContent>
              <Label
                direction={trend.borrow.direction}
                value={trend.borrow.value}
                hourly={hourly}
                apy="borrow"
                average={trend.borrow.average}
              />
            </TooltipContent>
          </Tooltip>
        ) : (
          borrow
        )
      ) : null}

      {props.averageAPY ? (
        <LegendItem value={props.averageAPY} label="Average APR" align="left" color="bg-slate-50" />
      ) : null}
    </Legend>
  );
};

const Label = (props: {
  direction: Trend['direction'];
  value: number;
  hourly?: boolean;
  apy: 'borrow' | 'deposit';
  average: number;
}) => {
  const { direction, value, hourly, apy, average } = props;

  return (
    <div className="text-stone-300 text-sm w-52 space-y-[10px]">
      <p>
        The <span className="font-medium text-white">{apy} APR</span> is currently {trendDirectionToVerb(direction)}{' '}
        {formatAsPreciseDecimalsWithDecimals(value)} percentage points per {hourly ? 'hour' : 'day'}.
      </p>
      <p>
        <span className="font-medium text-white">The average APR</span> is {pointsToPrecisePctWithDecimals(average)}
      </p>
    </div>
  );
};
